import React from 'react';
import { Link } from 'react-router-dom';
import {
    Smartphone,
    Search,
    Zap,
    Layers,
    BarChart3,
    Cookie,
    ShieldCheck,
    Lock,
    FileText,
    ArrowRight,
} from 'lucide-react';
import { PageHero, FadeIn, SectionLabel } from '../components/Shared';
import FloatingShapesBg from '../components/bg/FloatingShapesBg';

const Services = () => {
    return (
        <main className="bg-white text-[#1A1A2E] pb-24" data-testid="services-page">
            <PageHero
                label="Our services"
                title="Everything your business needs online."
                subtitle="Built and protected by experts. From your first homepage to your hundredth security audit."
                testId="services-hero"
                bg={<FloatingShapesBg />}
            />

            {/* ============== WEB DEV ============== */}
            <section className="py-20 bg-white" data-testid="web-dev-section">
                <div className="max-w-7xl mx-auto px-6 lg:px-8">
                    <FadeIn>
                        <SectionLabel>Service 01 · Web</SectionLabel>
                        <h2 className="mt-5 font-display text-3xl sm:text-4xl md:text-5xl font-semibold tracking-tight text-[#1A1A2E] max-w-3xl leading-[1.15]">
                            Custom website development.
                        </h2>
                        <p className="mt-5 text-[17px] text-[#4A4A5A] max-w-3xl leading-relaxed">
                            Every site we build is handcrafted for your brand. No templates, no
                            shortcuts. Whether you need a sleek 5-page business site or a
                            comprehensive 8+ page platform with blog, e-commerce, and
                            integrations — we deliver quality that converts.
                        </p>
                    </FadeIn>

                    <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {[
                            { icon: Smartphone, title: 'Responsive Design', desc: 'Pixel-perfect on every device, from mobile to desktop.' },
                            { icon: Search, title: 'SEO Optimisation', desc: 'Built for Google from day one. Meta tags, schema markup, fast load times.' },
                            { icon: Zap, title: 'Performance', desc: 'Sub-2-second load times. Optimised images, clean code, CDN delivery.' },
                            { icon: Layers, title: 'Content Management', desc: 'Easy-to-use CMS so you can update your own content anytime.' },
                            { icon: BarChart3, title: 'Analytics & Tracking', desc: 'Google Analytics 4, conversion tracking, and heatmaps setup.' },
                            { icon: Cookie, title: 'GDPR Compliance', desc: 'Cookie consent, privacy policy, and data handling built in.' },
                        ].map((f, i) => (
                            <FadeIn key={i} delay={(i % 3) * 0.08}>
                                <div
                                    className="p-7 bg-white border border-[#E8E8EC] rounded-2xl hover:shadow-card-hover hover:-translate-y-1 transition-all duration-500 h-full"
                                    data-testid={`web-feature-${i + 1}`}
                                >
                                    <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-[#F2F0FE]">
                                        <f.icon className="w-5 h-5 text-[#7B61FF]" />
                                    </div>
                                    <h3 className="mt-5 font-display text-[19px] font-semibold tracking-tight text-[#1A1A2E]">
                                        {f.title}
                                    </h3>
                                    <p className="mt-3 text-[#4A4A5A] text-[15px] leading-relaxed">
                                        {f.desc}
                                    </p>
                                </div>
                            </FadeIn>
                        ))}
                    </div>
                </div>
            </section>

            {/* ============== SECURITY ============== */}
            <section className="py-20 bg-[#F7F7F8]" data-testid="security-services-section">
                <div className="max-w-7xl mx-auto px-6 lg:px-8">
                    <FadeIn>
                        <SectionLabel>Service 02 · Security</SectionLabel>
                        <h2 className="mt-5 font-display text-3xl sm:text-4xl md:text-5xl font-semibold tracking-tight text-[#1A1A2E] max-w-3xl leading-[1.15]">
                            AI-powered security — <span className="text-[#00B89E]">£49/mo.</span>
                        </h2>
                        <p className="mt-5 text-[17px] text-[#4A4A5A] max-w-3xl leading-relaxed">
                            Your website is an asset. Protect it like one.
                        </p>
                    </FadeIn>

                    <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6">
                        {[
                            {
                                icon: ShieldCheck,
                                title: 'Code Audit & Vulnerability Scan',
                                desc: "Every month, our AI-powered system analyses your website's source code for security vulnerabilities including XSS (Cross-Site Scripting), SQL Injection, CSRF attacks, and data exposure risks. You receive a detailed report with severity ratings and recommended fixes, all implemented by our team.",
                            },
                            {
                                icon: Lock,
                                title: 'SSL & Security Headers Check',
                                desc: "We verify your SSL certificate validity, check all security headers (Content-Security-Policy, HSTS, X-Frame-Options, X-Content-Type-Options), and ensure your site meets current browser security standards. Any issues are flagged and resolved immediately.",
                            },
                            {
                                icon: FileText,
                                title: 'Monthly Report & Dependency Updates',
                                desc: "Receive a comprehensive monthly security report with a risk score (0-100), a summary of all checks performed, plugin and dependency updates applied, and a best-practices checklist. Full transparency, every month, delivered to your inbox.",
                            },
                        ].map((s, i) => (
                            <FadeIn key={i} delay={i * 0.1}>
                                <div
                                    className="p-7 bg-white border border-[#E8E8EC] rounded-2xl hover:shadow-card-hover hover:-translate-y-1 transition-all duration-500 h-full"
                                    data-testid={`security-card-${i + 1}`}
                                >
                                    <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-[#E6FBF6]">
                                        <s.icon className="w-5 h-5 text-[#00B89E]" />
                                    </div>
                                    <h3 className="mt-5 font-display text-[19px] font-semibold tracking-tight text-[#1A1A2E]">
                                        {s.title}
                                    </h3>
                                    <p className="mt-4 text-[#4A4A5A] text-[15px] leading-relaxed">
                                        {s.desc}
                                    </p>
                                </div>
                            </FadeIn>
                        ))}
                    </div>

                    <div className="mt-14 flex justify-center">
                        <Link
                            to="/contact"
                            className="inline-flex items-center gap-2 bg-[#1A1A2E] text-white font-semibold text-[15px] px-7 py-3.5 rounded-full hover:bg-[#7B61FF] transition-all duration-300"
                            data-testid="services-cta-security"
                        >
                            Start Your Security Plan <ArrowRight className="w-4 h-4" />
                        </Link>
                    </div>
                </div>
            </section>
        </main>
    );
};

export default Services;
